/**
 * The screens — the client's original eight plus Master Management (R10, 29 Sep), which sits
 * beside Field Management and has no mockup number. The sidebar draws them by `NAV_GROUPS`;
 * since Wave 5 the two Rule Management screens are a rule set's tabs, not sidebar entries.
 *
 * Order and labels come from the `SCREENS` array in `docs/console-mockup/build.mjs`. The
 * mockup's numbers (1–8) are deliberately NOT shown: the menu is grouped by how the work
 * actually flows, so a non-contiguous "3, 7, 5" in the sidebar reads as a bug. The numbers
 * survive in `mockupNumber` because meetings refer to "item 4".
 */

export type ScreenId = 'dash' | 'rb' | 'pb' | 'fields' | 'masters' | 'report' | 'alert' | 'monitor' | 'log' | 'investigate'
  // 2c rule sets
  | 'rulesets';

export interface ScreenDef {
  id: ScreenId;
  /** The route path. Deep-linkable; the sidebar is just links to these. */
  path: string;
  /** i18n key for the sidebar label — `nav.<id>.label`. */
  labelKey: string;
  /** The item number in the client's original eight-item list. Not rendered in the nav. Absent on a screen added since. */
  mockupNumber?: number;
}

export const SCREENS: readonly ScreenDef[] = [
  { id: 'dash', path: '/dashboard', labelKey: 'nav.dash.label', mockupNumber: 1 },
  // Wave 5: Transaction and Pattern base are a SET's tabs (`/rule-sets/<set>/transaction|pattern`),
  // not sidebar entries. These paths still answer: they redirect to the primary set's tab.
  { id: 'rb', path: '/rules/transaction', labelKey: 'nav.rb.label', mockupNumber: 2 },
  { id: 'pb', path: '/rules/pattern', labelKey: 'nav.pb.label', mockupNumber: 3 },
  // 2c rule sets — which sets evaluate every transaction, the primary, create / switch / archive.
  // Wave 5: the only way into a set's rules.
  { id: 'rulesets', path: '/rule-sets', labelKey: 'rulesets.nav.label' },
  { id: 'fields', path: '/fields', labelKey: 'nav.fields.label', mockupNumber: 4 },
  { id: 'masters', path: '/masters', labelKey: 'master.nav.label' },
  { id: 'report', path: '/report', labelKey: 'nav.report.label', mockupNumber: 5 },
  { id: 'alert', path: '/alerts', labelKey: 'nav.alert.label', mockupNumber: 6 },
  { id: 'monitor', path: '/monitor', labelKey: 'nav.monitor.label', mockupNumber: 7 },
  { id: 'log', path: '/log', labelKey: 'nav.log.label', mockupNumber: 8 },
  // R9 cases — Investigate: cases on a decision, their workflow, and rule precision from the labels.
  { id: 'investigate', path: '/investigate', labelKey: 'cases.nav.label' },
];

/** Where `/` lands. */
export const DEFAULT_PATH = '/dashboard';

export function screenByPath(path: string): ScreenDef | undefined {
  return SCREENS.find((s) => s.path === path);
}

// ---- R8 users ---------------------------------------------------------------

/**
 * Screens shown only to an Admin (`canManageUsers`), after the ones above. Kept apart from
 * `SCREENS` because `SCREENS` is the client's screen list (every entry is on every operator's
 * menu, in the mockup's order); User Management is on nobody's menu but an Admin's.
 */
export interface AdminScreenDef {
  // Wave 4 API clients: 'apikeys'
  id: 'users' | 'apikeys';
  path: string;
  labelKey: string;
}

export const ADMIN_SCREENS: readonly AdminScreenDef[] = [
  { id: 'users', path: '/users', labelKey: 'users.nav.label' },
  // Wave 4 API clients — one key per caller of the decision API.
  { id: 'apikeys', path: '/api-keys', labelKey: 'apikeys.nav.label' },
];

// ---- UX review P2-11: the sidebar in groups ------------------------------------

/**
 * The sidebar groups its links under small headings, in this order — what is happening first
 * (monitoring, then investigation), then the rules, then administration. `SCREENS` keeps the
 * client's screen list (and the mockup's order); this is only how the menu is drawn. Users is
 * in Admin, and only on an Admin's menu. A group heading is a LABEL, so it is Thai in Thai; the
 * links are screen names, English in both locales (catalogue rule 1).
 */
export type NavGroupId = 'rules' | 'monitoring' | 'investigate' | 'admin';

export interface NavGroup {
  id: NavGroupId;
  labelKey: string;
  items: readonly (ScreenId | AdminScreenDef['id'])[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  { id: 'monitoring', labelKey: 'nav.group.monitoring', items: ['dash', 'monitor', 'report', 'log'] },
  { id: 'investigate', labelKey: 'nav.group.investigate', items: ['investigate'] },
  // Wave 5: a set is the workspace — Transaction / Pattern base are its tabs, not menu entries.
  { id: 'rules', labelKey: 'nav.group.rules', items: ['rulesets', 'fields'] },
  { id: 'admin', labelKey: 'nav.group.admin', items: ['masters', 'alert', 'users', 'apikeys'] },
];

/**
 * The short name of a screen where the full one is too long: "Rule Management (Transaction)" is
 * just "Transaction". Wave 5: these are the set page's base tabs; a sidebar link that had one
 * kept the full screen name (`labelKey`) as its accessible name.
 */
export const NAV_SHORT_LABEL: Partial<Record<ScreenId, string>> = {
  rb: 'nav.rb.short',
  pb: 'nav.pb.short',
};
